import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { FaChevronDown, FaPaperclip, FaSmile, FaPaperPlane, FaTimes, FaChevronRight, FaChevronLeft, FaEye } from 'react-icons/fa';
import AxiosInstance from 'axiosConfig';

const BroadcastMessages = () => {
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    purpose: '',
    filterType: 'mainContact',
    customForm: '',
    selectedField: '',
    message: ''
  });

  // UI state
  const [selectedChannels, setSelectedChannels] = useState([]);
  const [activeTab, setActiveTab] = useState('setup');
  const [currentStep, setCurrentStep] = useState(1);
  const [characterCount, setCharacterCount] = useState(0);
  const [isSending, setIsSending] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState('');

  // API data state
  const [availableForms, setAvailableForms] = useState([]);
  const [availableFields, setAvailableFields] = useState([]);
  const [campaigns, setCampaigns] = useState({});
  const [campaignLogs, setCampaignLogs] = useState({});
  const [viewingMessage, setViewingMessage] = useState(null);

  // Loading states
  const [isLoadingForms, setIsLoadingForms] = useState(false);
  const [isLoadingFields, setIsLoadingFields] = useState(false);
  const [isLoadingCampaigns, setIsLoadingCampaigns] = useState(false);
  const [isLoadingLogs, setIsLoadingLogs] = useState(false);
  const [isLoadingMessage, setIsLoadingMessage] = useState(false);

  //Custom Query on Campaigns setup
  const [showCustomFilter, setShowCustomFilter] = useState(false);
  const [availableFilterFields, setAvailableFilterFields] = useState([]);
  const [rawFilterQuery, setRawFilterQuery] = useState('WHERE ');
  const [formattedFilterQuery, setFormattedFilterQuery] = useState('WHERE ');

  // Available channels
  const channels = useMemo(() => [
    { id: 'whatsapp', name: 'WhatsApp' },
    { id: 'sms', name: 'SMS' },
    { id: 'email', name: 'Email' },
    { id: 'telegram', name: 'Telegram' },
    { id: 'facebook', name: 'Facebook' },
    { id: 'twitter', name: 'X (Twitter)' }
  ], []);

  // Available variables
  const defaultVariables = useMemo(() => [
    '@CustomerName',
    '@CustomerOrder',
    '@OrderDate',
    '@CustomerPhone',
    '@CustomerEmail'
  ], []);

  const [availableVariables, setAvailableVariables] = useState(defaultVariables);

  // Helper function to format SQL keywords
  const formatSqlQuery = useCallback((query) => {
    return query
      .replace(/(WHERE|AND|IN)/gi, '<span class="text-blue-600 font-bold">$1</span>')
      .replace(/field_sn\d+/g, match => `<span class="text-green-600">${match}</span>`);
  }, []);

  // API functions
  const fetchAvailableForms = useCallback(async () => {
    setIsLoadingForms(true);
    try {
      const response = await AxiosInstance.get('/get-conversations');
      const forms = Object.entries(response.data).map(([id, name]) => ({ id, name }));
      setAvailableForms(forms);
    } catch (error) {
      console.error('Error fetching forms:', error);
    } finally {
      setIsLoadingForms(false);
    }
  }, []);

  const fetchFormFields = useCallback(async (formId) => {
    setIsLoadingFields(true);
    try {
      const response = await AxiosInstance.get(`/get-form-fields/${formId}`);
      const fields = Object.entries(response.data).map(([id, name]) => ({ id, name }));
      setAvailableFields(fields);
      setAvailableFilterFields(fields);
    } catch (error) {
      console.error('Error fetching form fields:', error);
    } finally {
      setIsLoadingFields(false);
    }
  }, []);

  const fetchCampaigns = useCallback(async () => {
    setIsLoadingCampaigns(true);
    try {
      const response = await AxiosInstance.get('/get_broadcast_campaigns');
      setCampaigns(response.data);
    } catch (error) {
      console.error('Error fetching campaigns:', error);
    } finally {
      setIsLoadingCampaigns(false);
    }
  }, []);

  const fetchCampaignLogs = useCallback(async (campaignId) => {
    if (!campaignId) return;

    setIsLoadingLogs(true);
    try {
      const response = await AxiosInstance.post('/get_campaign_send_logs', {
        campaign_id: campaignId
      });
      setCampaignLogs(response.data);
    } catch (error) {
      console.error('Error fetching campaign logs:', error);
    } finally {
      setIsLoadingLogs(false);
    }
  }, []);

  const fetchMessageContent = useCallback(async (messageId) => {
    setIsLoadingMessage(true);
    try {
      const response = await AxiosInstance.post('/get_message_content', {
        message_id: messageId
      });
      setViewingMessage(response.data || 'No message content available');
    } catch (error) {
      console.error('Error fetching message content:', error);
      setViewingMessage('Failed to load message content');
    } finally {
      setIsLoadingMessage(false);
    }
  }, []);

  // Fetch data based on tab and selections
  useEffect(() => {
    if (activeTab === 'setup' && formData.filterType === 'customForm') {
      fetchAvailableForms();
    } else if (activeTab === 'logs') {
      fetchCampaigns();
    }
  }, [activeTab, formData.filterType, fetchAvailableForms, fetchCampaigns]);

  // For custom query
  useEffect(() => {
    if (formData.customForm) {
      fetchFormFields(formData.customForm);
    } else {
      setAvailableFields([]);
      setAvailableFilterFields([]);
      setFormData(prev => ({ ...prev, selectedField: '' }));
    }
  }, [formData.customForm, fetchFormFields]);

  useEffect(() => {
    if (formData.selectedField) {
      const fieldVariable = `@${formData.selectedField.replace(/_/g, '')}`;
      if (!availableVariables.includes(fieldVariable)) {
        setAvailableVariables([...defaultVariables, fieldVariable]);
      }
    } else {
      setAvailableVariables(defaultVariables);
    }
  }, [formData.selectedField, availableVariables, defaultVariables]);

  const filterQueryRef = useRef(null);

  // Event handlers
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
      ...(name === 'customForm' && { selectedField: '' })
    }));

    if (name === 'message') {
      setCharacterCount(value.length);
    }
  };

  const handleChannelSelect = (e) => {
    const channelId = e.target.value;
    if (!channelId) return;

    const channel = channels.find(c => c.id === channelId);
    if (channel && !selectedChannels.some(c => c.id === channel.id)) {
      setSelectedChannels(prev => [...prev, channel]);
    }
    e.target.value = '';
  };

  const removeChannel = (channelId) => {
    setSelectedChannels(prev => prev.filter(c => c.id !== channelId));
  };

  const handleVariableDragStart = (e, variable) => {
    e.dataTransfer.setData('text/plain', variable);
  };

  const handleMessageDrop = (e) => {
    e.preventDefault();
    const variable = e.dataTransfer.getData('text/plain');
    const textarea = e.target;
    const startPos = textarea.selectionStart;
    const endPos = textarea.selectionEnd;

    const newValue = textarea.value.substring(0, startPos) +
      variable +
      textarea.value.substring(endPos);

    setFormData(prev => ({ ...prev, message: newValue }));
    setCharacterCount(newValue.length);
  };

  const handleDragOver = (e) => e.preventDefault();

  const sendBroadcast = async () => {
    if (!formData.name || selectedChannels.length === 0 || !formData.message) {
      alert('Please fill all required fields and select at least one channel');
      return;
    }

    if (formData.filterType === 'customForm' && !formData.customForm) {
      alert('Please select a form');
      return;
    }

    setIsSending(true);

    try {
      const selectedFieldObj = availableFields.find(field => field.name === formData.selectedField);
      const payload = {
        message_template: formData.message.replace(/@CustomerName/g, '/Name/'),
        choosen_channels: selectedChannels.map(c => c.id),
        contact_type: formData.filterType === 'mainContact' ? 'main' : 'custom',
        custom: formData.filterType === 'customForm' ? {
          form_no: parseInt(formData.customForm),
          field_sn: selectedFieldObj ? parseInt(selectedFieldObj.id) : null
        } : null,
        custom_query: formData.filterType === 'customForm' && rawFilterQuery !== 'WHERE ' ? rawFilterQuery : null
      };

      console.log('Sending payload:', payload);

      await AxiosInstance.post('/initiate_broadcast', payload);

      // Reset form after successful send
      setFormData({
        name: '',
        purpose: '',
        filterType: 'mainContact',
        customForm: '',
        selectedField: '',
        message: ''
      });
      setSelectedChannels([]);
      setCharacterCount(0);
      setCurrentStep(1);
      setRawFilterQuery('WHERE ');
      setFormattedFilterQuery('WHERE ');
    } catch (error) {
      console.error('Error sending broadcast:', error);
      alert('Failed to send broadcast');
    } finally {
      setIsSending(false);
    }
  };

  // Add these new handlers for custom filter
  const handleFilterDragStart = (e, field) => {
    e.dataTransfer.setData('text/plain', `field_sn${field.id}`);
  };

  const handleFilterDrop = (e) => {
    e.preventDefault();
    const field = e.dataTransfer.getData('text/plain');

    // Get current selection
    const selection = window.getSelection();
    if (!selection.rangeCount) return;

    const range = selection.getRangeAt(0);
    range.deleteContents();

    // Insert the dragged field
    const textNode = document.createTextNode(` ${field} `);
    range.insertNode(textNode);

    // Update cursor position
    range.setStartAfter(textNode);
    range.setEndAfter(textNode);
    selection.removeAllRanges();
    selection.addRange(range);

    // Update state
    const newQuery = filterQueryRef.current.textContent;
    setRawFilterQuery(newQuery);
    setFormattedFilterQuery(formatSqlQuery(newQuery));
  };

  const renderCustomFilter = () => (
    <div className="mb-4">
      <div
        className="flex items-center cursor-pointer text-blue-600 mb-2"
        onClick={() => setShowCustomFilter(!showCustomFilter)}
      >
        <span>Advanced Filter</span>
        <FaChevronDown className={`ml-2 transition-transform ${showCustomFilter ? 'transform rotate-180' : ''}`} />
      </div>

      {showCustomFilter && (
        <div className="border rounded-md p-4 bg-gray-50">
          <div className="mb-3">
            <label className="block text-sm font-medium text-gray-700 mb-1">Available Fields</label>
            <div className="flex flex-wrap gap-2">
              {availableFilterFields.map(field => (
                <span
                  key={`filter-${field.id}`}
                  draggable
                  onDragStart={(e) => handleFilterDragStart(e, field)}
                  className="px-3 py-1 bg-blue-100 text-blue-800 Blooms rounded-full text-sm cursor-move hover:bg-blue-200"
                >
                  {field.name.replace(/_/g, ' ')}
                </span>
              ))}
            </div>
            <p className="text-xs text-gray-500 mt-1">Drag fields into the query below</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Custom Filter Query</label>
            <div
              ref={filterQueryRef}
              className="w-full p-3 border border-gray-300 rounded-md bg-white min-h-20"
              contentEditable
              onDrop={handleFilterDrop}
              onDragOver={handleDragOver}
              onInput={(e) => {
                const newQuery = e.target.textContent;
                setRawFilterQuery(newQuery);
                setFormattedFilterQuery(formatSqlQuery(newQuery));
              }}
              dangerouslySetInnerHTML={{ __html: formattedFilterQuery }}
              suppressContentEditableWarning={true}
            />
            <p className="text-xs text-gray-500 mt-1">
              Start with WHERE clause. Fields will be converted to field_snX format.
            </p>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div className="bg-gray-50 min-h-screen p-6">
      <div className="max-w-4xl mx-auto bg-white rounded-lg shadow-md overflow-hidden">
        {/* Tabs */}
        <div className="flex border-b">
          <button
            onClick={() => setActiveTab('setup')}
            className={`px-6 py-3 font-medium ${activeTab === 'setup' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-600 hover:text-gray-800'}`}
          >
            Broadcast Setup
          </button>
          <button
            onClick={() => setActiveTab('logs')}
            className={`px-6 py-3 font-medium ${activeTab === 'logs' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-600 hover:text-gray-800'}`}
          >
            Send Logs
          </button>
        </div>

        {activeTab === 'setup' ? (
          <div className="p-6">
            {currentStep === 1 ? (
              <div>
                <h2 className="text-xl font-bold text-gray-800 mb-4">Campaign Setup</h2>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Campaign Name *</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      className="w-full p-2 border border-gray-300 rounded-md"
                      placeholder="e.g. Summer Sale Announcement"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Purpose</label>
                    <input
                      type="text"
                      name="purpose"
                      value={formData.purpose}
                      onChange={handleInputChange}
                      className="w-full p-2 border border-gray-300 rounded-md"
                      placeholder="e.g. Notify customers about new products"
                    />
                  </div>
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Select Recipients</label>
                  <div className="flex space-x-4">
                    <label className="inline-flex items-center">
                      <input
                        type="radio"
                        name="filterType"
                        value="mainContact"
                        checked={formData.filterType === 'mainContact'}
                        onChange={handleInputChange}
                        className="h-4 w-4 text-blue-600"
                      />
                      <span className="ml-2 text-gray-700">Main Customer Contact</span>
                    </label>
                    <label className="inline-flex items-center">
                      <input
                        type="radio"
                        name="filterType"
                        value="customForm"
                        checked={formData.filterType === 'customForm'}
                        onChange={handleInputChange}
                        className="h-4 w-4 text-blue-600"
                      />
                      <span className="ml-2 text-gray-700">Custom Data Form</span>
                    </label>
                  </div>
                </div>

                {formData.filterType === 'customForm' && (
                  <>
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Select Form *</label>
                      <select
                        name="customForm"
                        value={formData.customForm}
                        onChange={handleInputChange}
                        className="w-full p-2 border border-gray-300 rounded-md"
                        required
                        disabled={isLoadingForms}
                      >
                        <option value="">{isLoadingForms ? 'Loading forms...' : 'Select a form'}</option>
                        {availableForms.map(form => (
                          <option key={form.id} value={form.id}>{form.name}</option>
                        ))}
                      </select>
                    </div>

                    {formData.customForm && (
                      <div className="mb-6">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Select Contact Field *</label>
                        <select
                          name="selectedField"
                          value={formData.selectedField}
                          onChange={handleInputChange}
                          className="w-full p-2 border border-gray-300 rounded-md"
                          required
                          disabled={isLoadingFields}
                        >
                          <option value="">{isLoadingFields ? 'Loading fields...' : 'Select a contact field'}</option>
                          {availableFields.map(field => (
                            <option key={field.id} value={field.name}>
                              {field.name.replace(/_/g, ' ')}
                            </option>
                          ))}
                        </select>
                        <p className="text-xs text-gray-500 mt-1">
                          Select which field contains the contact information (email, phone, etc.)
                        </p>
                      </div>
                    )}
                    {formData.customForm && renderCustomFilter()}
                  </>
                )}

                <div className="flex justify-end mt-6">
                  <button
                    onClick={() => setCurrentStep(2)}
                    disabled={!formData.name || (formData.filterType === 'customForm' && (!formData.customForm || !formData.selectedField))}
                    className="flex items-center px-6 py-2 rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:bg-blue-400"
                  >
                    Next <FaChevronRight className="ml-2" />
                  </button>
                </div>
              </div>
            ) : (
              <div>
                <h2 className="text-xl font-bold text-gray-800 mb-4">Message Composition</h2>

                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Select Channels *</label>
                  <select
                    onChange={handleChannelSelect}
                    className="w-full p-2 border border-gray-300 rounded-md"
                  >
                    <option value="">Select a channel to add</option>
                    {channels.map(channel => (
                      <option
                        key={channel.id}
                        value={channel.id}
                        disabled={selectedChannels.some(c => c.id === channel.id)}
                      >
                        {channel.name}
                      </option>
                    ))}
                  </select>

                  {selectedChannels.length > 0 && (
                    <div className="mt-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Selected Channels</label>
                      <div className="flex flex-wrap gap-2">
                        {selectedChannels.map(channel => (
                          <div key={channel.id} className="flex items-center bg-blue-100 text-blue-800 rounded-full px-3 py-1">
                            <span>{channel.name}</span>
                            <button
                              onClick={() => removeChannel(channel.id)}
                              className="ml-2 text-blue-600 hover:text-blue-800"
                            >
                              <FaTimes className="text-xs" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <div className="mb-3">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Available Variables</label>
                  <div className="flex flex-wrap gap-2">
                    {availableVariables.map(variable => (
                      <span
                        key={variable}
                        draggable
                        onDragStart={(e) => handleVariableDragStart(e, variable)}
                        className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm cursor-move hover:bg-blue-200"
                      >
                        {variable}
                      </span>
                    ))}
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Drag and drop variables into the message</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Message *</label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    onDrop={handleMessageDrop}
                    onDragOver={handleDragOver}
                    className="w-full h-40 p-3 border border-gray-300 rounded-md"
                    placeholder="Type your message here..."
                    required
                  />
                  <div className="flex justify-between items-center mt-1">
                    <div className="flex space-x-2">
                      <button className="text-gray-500 hover:text-gray-700">
                        <FaPaperclip />
                      </button>
                      <button className="text-gray-500 hover:text-gray-700">
                        <FaSmile />
                      </button>
                    </div>
                    <span className={`text-sm ${characterCount > 500 ? 'text-red-500' : 'text-gray-500'}`}>
                      {characterCount}/500 characters
                    </span>
                  </div>
                </div>

                <div className="flex justify-between mt-6">
                  <button
                    onClick={() => setCurrentStep(1)}
                    className="flex items-center px-6 py-2 rounded-md bg-gray-200 text-gray-700 hover:bg-gray-300"
                  >
                    <FaChevronLeft className="mr-2" /> Back
                  </button>
                  <button
                    onClick={sendBroadcast}
                    disabled={isSending || selectedChannels.length === 0 || !formData.message}
                    className={`flex items-center px-6 py-2 rounded-md text-white ${isSending ? 'bg-blue-400' : 'bg-blue-600 hover:bg-blue-700'} disabled:bg-blue-400`}
                  >
                    {isSending ? 'Sending...' : 'Send Broadcast'}
                    <FaPaperPlane className="ml-2" />
                  </button>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="p-6">
            <h2 className="text-xl font-bold text-gray-800 mb-4">Send Logs</h2>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Filter by Campaign</label>
              <select
                value={selectedCampaign}
                onChange={(e) => {
                  setSelectedCampaign(e.target.value);
                  fetchCampaignLogs(e.target.value);
                }}
                className="w-full p-2 border border-gray-300 rounded-md"
                disabled={isLoadingCampaigns}
              >
                <option value="">All Campaigns</option>
                {Object.entries(campaigns).map(([id, name]) => (
                  <option key={id} value={id}>{name}</option>
                ))}
              </select>
            </div>

            {isLoadingLogs ? (
              <p className="text-gray-500 text-center py-4">Loading logs...</p>
            ) : Object.keys(campaignLogs).length === 0 ? (
              <p className="text-gray-500 text-center py-4">
                {selectedCampaign ? 'No logs available for this campaign' : 'Select a campaign to view logs'}
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Timestamp</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Channel Used</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Contact Used</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Action</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {Object.entries(campaignLogs).map(([messageId, log]) => (
                      <tr key={messageId}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{log.timestamp}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 capitalize">{log.channel_used}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{log.contact_used}</td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            log.status === 'sent' ? 'bg-green-100 text-green-800' :
                            log.status === 'failed' ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'
                          }`}>
                            {log.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          <button
                            onClick={() => fetchMessageContent(messageId)}
                            className="text-blue-600 hover:text-blue-900"
                            disabled={isLoadingMessage}
                          >
                            <FaEye />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Message View Modal */}
      {viewingMessage && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">Sent Message</h3>
                <button
                  onClick={() => setViewingMessage(null)}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <FaTimes />
                </button>
              </div>
              <div className="bg-gray-50 p-4 rounded-md whitespace-pre-wrap">
                {isLoadingMessage ? 'Loading message...' : viewingMessage}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BroadcastMessages;